/* ==========================================================================
   Recherche prédictive — Holograbme (refonte 2026-07-23)

   Rendu du panneau : sections/predictive-search.liquid
   Formulaire       : snippets/predictive-search.liquid (dans l'overlay search-modal)

   Ce qui change par rapport au stock Speedfly :
     · open() n'est plus inconditionnel -> plus de boîte vide (le panneau ne s'ouvre que
       si la section a rendu quelque chose ; l'état « aucun résultat » est du contenu,
       donc il s'ouvre, mais un rendu réellement vide ferme le panneau).
     · navigation clavier complète : ArrowDown / ArrowUp / Home / End / Enter / Escape / Tab.
     · combobox conforme : aria-expanded, aria-activedescendant, aria-selected roving.
     · annonce du nombre de résultats aux lecteurs d'écran (région live polie).
     · requêtes concurrentes annulées (AbortController) -> plus de résultats hors séquence.
     · fermeture au clic extérieur.
     · les articles ne sont plus demandés à l'API (décision produit : produits/collections/pages).

   Conservé : nom du custom element, seuil de 3 caractères, debounce 300 ms,
   fonctions globales openSearch()/closeSearch() (appelées en onclick inline dans
   snippets/header2.liquid et snippets/mobile-navbar.liquid).
   ========================================================================== */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.status = this.querySelector('[data-ps-status]');

    this.activeIndex = -1;
    this.controller = null;

    if (!this.input || !this.predictiveSearchResults) return;

    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    // Clic extérieur : referme le panneau sans fermer l'overlay.
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  /* --- Données ---------------------------------------------------------- */

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }
    if (searchTerm.length >= 3) {
      this.getSearchResults(searchTerm);
    }
  }

  getSearchResults(searchTerm) {
    // Une seule requête en vol : la précédente est annulée.
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    const url = `/search/suggest?q=${encodeURIComponent(searchTerm)}`
      + `&resources[type]=product,collection,page`
      + `&resources[limit]=6`
      + `&section_id=predictive-search`;

    fetch(url, { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        const markup = section ? section.innerHTML : '';

        // Rendu réellement vide -> on ne montre rien (garde-fou anti boîte blanche).
        if (!markup.trim()) {
          this.close();
          return;
        }

        this.predictiveSearchResults.innerHTML = markup;
        this.open();
        this.announce();
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        this.close();
      });
  }

  /* --- Ouverture / fermeture -------------------------------------------- */

  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
    this.resetActive();
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  isOpen() {
    return this.predictiveSearchResults.style.display === 'block';
  }

  /* --- Navigation clavier ----------------------------------------------- */

  get items() {
    return Array.from(this.predictiveSearchResults.querySelectorAll('[data-ps-nav]'));
  }

  resetActive() {
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.items.forEach((a) => {
      a.classList.remove('is-active');
      const li = a.closest('[role="option"]');
      if (li) li.setAttribute('aria-selected', 'false');
    });
  }

  setActive(index) {
    const items = this.items;
    if (!items.length) return;

    // Boucle circulaire haut/bas.
    if (index < 0) index = items.length - 1;
    if (index >= items.length) index = 0;

    this.activeIndex = index;

    items.forEach((a, i) => {
      const li = a.closest('[role="option"]');
      const active = i === index;
      a.classList.toggle('is-active', active);
      if (li) li.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active && li && li.id) this.input.setAttribute('aria-activedescendant', li.id);
    });

    const current = items[index];
    if (current && current.scrollIntoView) {
      current.scrollIntoView({ block: 'nearest' });
    }
  }

  onKeydown(event) {
    // Échap referme d'abord le panneau, ensuite seulement l'overlay (2 pressions).
    // stopPropagation() évite que le handler document ferme l'overlay du même coup.
    if (event.key === 'Escape') {
      if (this.isOpen()) {
        event.stopPropagation();
        this.close();
      }
      return;
    }

    if (!this.isOpen()) {
      // Flèche bas sur un champ déjà rempli : rouvre les suggestions.
      if (event.key === 'ArrowDown' && this.input.value.trim().length >= 3) {
        event.preventDefault();
        this.getSearchResults(this.input.value.trim());
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActive(this.activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActive(this.activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.items.length - 1);
        break;
      case 'Enter': {
        // Sans sélection, on laisse le formulaire partir vers /search (comportement natif).
        const current = this.items[this.activeIndex];
        if (current) {
          event.preventDefault();
          current.click();
        }
        break;
      }
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  }

  /* --- Annonce lecteur d'écran ------------------------------------------ */

  announce() {
    if (!this.status) return;
    const panel = this.predictiveSearchResults.querySelector('[data-ps-panel]');
    const total = panel ? parseInt(panel.getAttribute('data-ps-total'), 10) : 0;

    let message;
    if (!total) {
      message = 'Aucune suggestion.';
    } else if (total === 1) {
      message = '1 suggestion disponible. Utilisez les flèches pour naviguer.';
    } else {
      message = total + ' suggestions disponibles. Utilisez les flèches pour naviguer.';
    }
    this.status.textContent = message;
  }

  /* --- Utilitaire -------------------------------------------------------- */

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);

/* --- Overlay plein écran (appelé en onclick inline, ne pas renommer) ------ */

function openSearch() {
  const overlay = document.getElementById('search-overlay');
  if (!overlay) return;

  // Mémorise le déclencheur pour rendre le focus à la fermeture.
  openSearch._trigger = document.activeElement;

  overlay.style.display = 'block';
  overlay.removeAttribute('aria-hidden');
  document.documentElement.classList.add('search-overlay-open');

  const input = overlay.querySelector('input[type="search"]') || document.getElementById('Search');
  if (input) setTimeout(function () { input.focus(); }, 120);
}

function closeSearch() {
  const overlay = document.getElementById('search-overlay');
  if (!overlay) return;

  overlay.style.display = 'none';
  overlay.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove('search-overlay-open');

  const ps = overlay.querySelector('predictive-search');
  if (ps && typeof ps.close === 'function') ps.close();

  const trigger = openSearch._trigger;
  if (trigger && typeof trigger.focus === 'function') trigger.focus();
}

// Échap ferme l'overlay même quand le focus n'est pas dans le champ.
document.addEventListener('keydown', function (event) {
  if (event.key !== 'Escape') return;
  const overlay = document.getElementById('search-overlay');
  if (overlay && overlay.style.display === 'block') closeSearch();
});
